/**
 * Page dashboard pour les utilisateurs non-admin.
 * Affiche les informations utilisateur et les cartes d'acces rapide.
 * Le header avec UserMenu est gere par UserLayout.
 */

import { useNavigate } from 'react-router-dom';
import { useAuth } from '@features/auth';

export function UserDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div>
      <div className="bg-surface rounded-lg shadow-lg p-8">
        <h2 className="text-4xl font-bold text-content mb-4">
          Bienvenue au Dashboard!
        </h2>

        {user && (
          <div className="mb-8 p-4 bg-accent-light border border-border rounded-lg">
            <p className="text-accent">
              <strong>Utilisateur:</strong> {user.prenom} {user.nom}
            </p>
            <p className="text-accent">
              <strong>Email:</strong> {user.email}
            </p>
            <p className="text-accent">
              <strong>Role:</strong> {user.role}
            </p>
            {user.deuxFacteursActive && (
              <p className="text-accent">
                <strong>2FA:</strong> Active ({user.deuxFacteursMethode})
              </p>
            )}
          </div>
        )}

        <div className="prose max-w-none">
          <h3 className="text-xl font-semibold text-content mb-3">
            Acces rapide
          </h3>
          <p className="text-content-muted mb-6">
            Utilisez le menu utilisateur en haut a droite pour acceder a vos parametres et gerer votre compte.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Carte 2FA */}
            <button
              onClick={() => navigate('/dashboard/2fa')}
              className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border border-border rounded-lg hover:shadow-md transition-shadow text-left"
            >
              <div className="text-3xl mb-2">🔒</div>
              <h4 className="text-lg font-semibold text-content mb-1">
                Securite & 2FA
              </h4>
              <p className="text-sm text-content-muted">
                Configurez l'authentification a deux facteurs
              </p>
            </button>

            {/* Carte Sessions */}
            <button
              onClick={() => navigate('/dashboard/sessions')}
              className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border border-border rounded-lg hover:shadow-md transition-shadow text-left"
            >
              <div className="text-3xl mb-2">📱</div>
              <h4 className="text-lg font-semibold text-content mb-1">
                Sessions actives
              </h4>
              <p className="text-sm text-content-muted">
                Gerez vos appareils connectes
              </p>
            </button>

            {/* Carte Compte */}
            <button
              onClick={() => navigate('/dashboard/account')}
              className="p-6 bg-gradient-to-br from-green-50 to-green-100 border border-border rounded-lg hover:shadow-md transition-shadow text-left"
            >
              <div className="text-3xl mb-2">👤</div>
              <h4 className="text-lg font-semibold text-content mb-1">
                Mon compte
              </h4>
              <p className="text-sm text-content-muted">
                Modifiez vos informations personnelles
              </p>
            </button>

            {/* Carte Parametres de securite */}
            <button
              onClick={() => navigate('/dashboard/security')}
              className="p-6 bg-gradient-to-br from-orange-50 to-orange-100 border border-border rounded-lg hover:shadow-md transition-shadow text-left"
            >
              <div className="text-3xl mb-2">🛡️</div>
              <h4 className="text-lg font-semibold text-content mb-1">
                Parametres de securite
              </h4>
              <p className="text-sm text-content-muted">
                Surveillez l'activite et gerez la securite
              </p>
            </button>
          </div>
        </div>

        {/* Section Gestion de la Paie */}
        <div className="mt-8">
          <h3 className="text-xl font-semibold text-content mb-3">
            Gestion de la Paie
          </h3>
          <p className="text-content-muted mb-6">
            Accédez aux fonctionnalités de gestion de la paie.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

            <button
              onClick={() => navigate('/paie/bulletins')}
              className="p-6 bg-gradient-to-br from-indigo-50 to-indigo-100 border border-border rounded-lg hover:shadow-md transition-shadow text-left"
            >
              <div className="text-3xl mb-2">📄</div>
              <h4 className="text-lg font-semibold text-content mb-1">Bulletins de paie</h4>
              <p className="text-sm text-content-muted">Consulter et générer les bulletins</p>
            </button>

            <button
              onClick={() => navigate('/paie/bulletins/masse')}
              className="p-6 bg-gradient-to-br from-violet-50 to-violet-100 border border-border rounded-lg hover:shadow-md transition-shadow text-left"
            >
              <div className="text-3xl mb-2">⚡</div>
              <h4 className="text-lg font-semibold text-content mb-1">Génération en masse</h4>
              <p className="text-sm text-content-muted">Générer tous les bulletins du mois</p>
            </button>

            <button
              onClick={() => navigate('/paie/employes')}
              className="p-6 bg-gradient-to-br from-cyan-50 to-cyan-100 border border-border rounded-lg hover:shadow-md transition-shadow text-left"
            >
              <div className="text-3xl mb-2">👥</div>
              <h4 className="text-lg font-semibold text-content mb-1">Employés</h4>
              <p className="text-sm text-content-muted">Gérer la liste des employés</p>
            </button>

            <button
              onClick={() => navigate('/paie/bulletins/tableau')}
              className="p-6 bg-gradient-to-br from-teal-50 to-teal-100 border border-border rounded-lg hover:shadow-md transition-shadow text-left"
            >
              <div className="text-3xl mb-2">📊</div>
              <h4 className="text-lg font-semibold text-content mb-1">État de rémunération</h4>
              <p className="text-sm text-content-muted">Tableau mensuel + export XLSX</p>
            </button>

            <button
              onClick={() => navigate('/paie/rapports')}
              className="p-6 bg-gradient-to-br from-sky-50 to-sky-100 border border-border rounded-lg hover:shadow-md transition-shadow text-left"
            >
              <div className="text-3xl mb-2">📈</div>
              <h4 className="text-lg font-semibold text-content mb-1">Rapports</h4>
              <p className="text-sm text-content-muted">Livre de paie, export comptable, audit</p>
            </button>

            <button
              onClick={() => navigate('/paie/configuration')}
              className="p-6 bg-gradient-to-br from-slate-50 to-slate-100 border border-border rounded-lg hover:shadow-md transition-shadow text-left"
            >
              <div className="text-3xl mb-2">⚙️</div>
              <h4 className="text-lg font-semibold text-content mb-1">Configuration</h4>
              <p className="text-sm text-content-muted">Barèmes, primes, taxes, simulateur</p>
            </button>

          </div>
        </div>
      </div>
    </div>
  );
}
